import { useState } from "react";
import { FileDown, Share2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { TooltipButton } from "@/components/common/Tooltips";
import { api } from "@/lib/api";
import { toast } from "sonner";

// Descarga / comparte el recibo de pago en PDF (GET /pagos/{id}/recibo-pdf).
// El backend aplica aislamiento por entidad y bloquea PII al super_admin.
export function ReciboPagoButton({ pago, size = "sm" }) {
  const [busy, setBusy] = useState(false);
  if (!pago?.id) return null;

  const nombre = `Recibo_${(pago.recibo || pago.id).toString().replace(/\s+/g, "_")}.pdf`;

  const fetchBlob = async () => {
    const { data } = await api.get(`/pagos/${pago.id}/recibo-pdf`, { responseType: "blob" });
    return new Blob([data], { type: "application/pdf" });
  };

  const descargar = async () => {
    setBusy(true);
    try {
      const blob = await fetchBlob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = nombre;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo generar el recibo");
    } finally {
      setBusy(false);
    }
  };

  const compartir = async () => {
    setBusy(true);
    try {
      const blob = await fetchBlob();
      const file = new File([blob], nombre, { type: "application/pdf" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `Recibo ${pago.recibo || ""}`,
          text: "Recibo de pago — FELVORA Compliance",
        });
      } else {
        // Fallback: abre el PDF en una pestaña nueva para compartir manualmente
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank", "noopener");
        setTimeout(() => URL.revokeObjectURL(url), 60000);
        toast.message("Compartir no está disponible en este dispositivo; abrí el recibo en una pestaña nueva.");
      }
    } catch (e) {
      if (e?.name !== "AbortError")
        toast.error(e?.response?.data?.detail || "No se pudo compartir el recibo");
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex items-center gap-1">
      <TooltipButton
        tooltip="Descargar el recibo del pago en PDF"
        data-testid={`recibo-pdf-btn-${pago.id}`}
        variant="ghost"
        size={size === "icon" ? "icon" : "sm"}
        className="h-8 gap-1.5 px-2.5 text-forest-700 hover:bg-emerald-50 hover:text-forest-800"
        onClick={descargar}
        disabled={busy}
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
        {size !== "icon" && "Recibo"}
      </TooltipButton>
      <TooltipButton
        tooltip="Compartir el recibo (por WhatsApp, correo, etc.)"
        data-testid={`recibo-share-btn-${pago.id}`}
        variant="ghost"
        size="icon"
        className="h-8 w-8 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
        onClick={compartir}
        disabled={busy}
      >
        <Share2 className="h-4 w-4" />
      </TooltipButton>
    </span>
  );
}
